import {
  DeliveredIntoDto,
  DeliveryDto,
  DeliveryDtoDeliveredAtHub,
  DeliveryDtoDeliveredBy,
  DeliveryDtoDeliveredTo,
  DeliveryDtoProject,
  DeliveryDtoWorkflow,
  KnownDeliveryDtoDeliveryChannel,
  KnownDeliveryDtoDeliveryType,
  KnownDeliveryState,
  KnownNotificationDtoLatestStatus,
  KnownPartyDtoType,
  NoteDto,
  NotificationDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { createPath, Paths } from '../../../app/router/app.router';
import { DeliveredStatusCircle } from '../../../ui/components/pure/icons/DeliveredStatusCircle.icon';
import { OverdueStatusCircle } from '../../../ui/components/pure/icons/OverduedStatusCircle.icon';
import { SVGIconComponent } from '../../../ui/components/pure/icons/types';
import { deliveryChannelToIcon } from '../maps/delivery-channel-to-icon.map';
import { deliveryStateToColor } from '../maps/delivery-state-to-color.map';
import { deliveryStateToLabel, getDeliveryStateLabelWithFlowType } from '../maps/delivery-state-to-label.map';
import { BookingFlowType } from '../../../ui/enums/booking-flow-type.enum';
import { deliveryTypeToIconMap } from '../maps/delivery-type-to-icon.map';
import { deliveryTypeToLabelMap } from '../maps/delivery-type-to-label.map';

const RETAIL_DELIVERY_TYPES: KnownDeliveryDtoDeliveryType[] = [
  KnownDeliveryDtoDeliveryType.RetailOrder,
  KnownDeliveryDtoDeliveryType.LockerpointOrder
];

export class Delivery {
  readonly id: string;
  readonly state: KnownDeliveryState;
  readonly deliveryType: KnownDeliveryDtoDeliveryType;
  readonly deliveryChannel?: KnownDeliveryDtoDeliveryChannel;
  readonly collectionCode?: string;
  readonly bookingTime?: Date;
  readonly createdTime: Date;
  readonly abandonedTime?: Date;
  readonly deliveredTime?: Date;
  readonly overdueTime?: Date;
  readonly completedTime?: Date;
  readonly collectedTime?: Date;
  readonly retrievedTime?: Date;
  readonly cancelledTime?: Date;
  readonly deliveryReference?: string;
  readonly barcode?: string;
  readonly carrierReference?: string;
  readonly isDeleted: boolean;
  readonly deletedTime?: Date;
  readonly deliveredTo?: DeliveryDtoDeliveredTo;
  readonly deliveredBy?: DeliveryDtoDeliveredBy;
  readonly migratedFromAc?: boolean;
  readonly migratedFromACTime?: Date;
  readonly migratedFromAcDeliveryId?: string;
  readonly workflow?: DeliveryDtoWorkflow;
  readonly project: DeliveryDtoProject;
  readonly deliveredAtHub?: DeliveryDtoDeliveredAtHub;
  readonly notifications?: NotificationDto[];
  readonly deliveredInto?: DeliveredIntoDto;
  readonly notes?: NoteDto[];

  constructor(dto: DeliveryDto) {
    this.id = dto.id;
    this.state = dto.state as KnownDeliveryState;
    this.deliveryType = dto.deliveryType as KnownDeliveryDtoDeliveryType;
    this.deliveryChannel = dto.deliveryChannel as KnownDeliveryDtoDeliveryChannel;
    this.collectionCode = dto.collectionCode;
    this.bookingTime = dto.bookingTime;
    this.createdTime = dto.createdTime;
    this.abandonedTime = dto.abandonedTime;
    this.deliveredTime = dto.deliveredTime;
    this.overdueTime = dto.overdueTime;
    this.completedTime = dto.completedTime;
    this.collectedTime = dto.collectedTime;
    this.retrievedTime = dto.retrievedTime;
    this.cancelledTime = dto.cancelledTime;
    this.deliveryReference = dto.deliveryReference;
    this.barcode = dto.barcode;
    this.carrierReference = dto.carrierReference;
    this.isDeleted = dto.isDeleted;
    this.deletedTime = dto.deletedTime;
    this.deliveredTo = dto.deliveredTo;
    this.deliveredBy = dto.deliveredBy;
    this.migratedFromAc = dto.migratedFromAc;
    this.migratedFromACTime = dto.migratedFromACTime;
    this.migratedFromAcDeliveryId = dto.migratedFromAcDeliveryId;
    this.workflow = dto.workflow;
    this.project = dto.project;
    this.deliveredAtHub = dto.deliveredAtHub;
    this.notifications = dto.notifications;
    this.deliveredInto = dto.deliveredInto;
    this.notes = dto.notes;
  }

  public get isInFinalState(): boolean {
    return [KnownDeliveryState.Collected, KnownDeliveryState.Cancelled, KnownDeliveryState.Retrieved].includes(
      this.state as KnownDeliveryState
    );
  }

  public get deliveryTypeLabel(): string {
    return deliveryTypeToLabelMap[this.deliveryType];
  }

  public get deliveryTypeIcon(): SVGIconComponent {
    return deliveryTypeToIconMap[this.deliveryType];
  }

  public get requiresAttentionStatusIcon(): SVGIconComponent | undefined {
    switch (this.state) {
      case KnownDeliveryState.Delivered:
        return DeliveredStatusCircle;
      case KnownDeliveryState.Overdue:
        return OverdueStatusCircle;
      default:
        return undefined;
    }
  }

  public get deliveryChannelIcon(): SVGIconComponent | undefined {
    return this.deliveryChannel && deliveryChannelToIcon[this.deliveryChannel];
  }

  public get stateLabel(): string {
    return deliveryStateToLabel[this.state];
  }

  public getStateLabelWithFlowType(bookingFlowType: BookingFlowType): string {
    return getDeliveryStateLabelWithFlowType(this.state, bookingFlowType);
  }

  public get stateColor(): string {
    return deliveryStateToColor[this.state];
  }

  public get deliveredByLabel(): string | undefined {
    return this.deliveredBy?.party?.name;
  }

  public get deliveredByAgentLabel() {
    return this.deliveredBy?.agent?.name || '';
  }

  public get unitLabel() {
    let unitLabel = '';
    if (
      this.deliveredTo?.party.type === KnownPartyDtoType.Occupant ||
      this.deliveredTo?.party.type === KnownPartyDtoType.Customer
    ) {
      unitLabel = this.deliveredTo?.parent?.name ?? '';
    }
    return unitLabel;
  }

  public get lockerName(): string {
    return this.deliveredInto?.name ?? '';
  }

  public get lockerSize(): string {
    return this.deliveredInto?.size ?? '';
  }

  public get isLatestNotificationError() {
    if (this.notifications && this.notifications.length > 0) {
      const latestNotification = this.notifications[this.notifications.length - 1];
      return [KnownNotificationDtoLatestStatus.Delayed, KnownNotificationDtoLatestStatus.Failed].includes(
        latestNotification?.latestStatus as KnownNotificationDtoLatestStatus
      );
    }
    return false;
  }

  /** Retail deliveries belong to an order and are managed from the order details page, not the delivery's. */
  public get isRetailType(): boolean {
    return RETAIL_DELIVERY_TYPES.includes(this.deliveryType);
  }

  /** A retail delivery whose order is known, so it can be linked to the order details page. */
  public isRetailWithOrder(): this is Delivery & { workflow: DeliveryDtoWorkflow } {
    return this.isRetailType && this.workflow !== undefined;
  }

  public createDetailsPageUrl(currentProjectShortcodeOrID: string): string | undefined {
    // Use the delivery project information first, then fallback to the current project
    const projectShortcodeOrID = this.project?.shortcode ?? this.project.id ?? currentProjectShortcodeOrID;
    if (this.isRetailType) {
      if (!this.isRetailWithOrder()) {
        return undefined;
      }
      return createPath(Paths.order, { projectID: projectShortcodeOrID, orderID: this.workflow.id });
    } else {
      return createPath(Paths.delivery, { projectID: projectShortcodeOrID, deliveryID: this.id });
    }
  }
}
